/**
 * Formatting helpers for currency / numbers.
 * Pure functions, locale-aware via Intl.
 */

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const usdFormatterCents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 2,
});

/**
 * Format a number as a whole-dollar USD string ("$12,345").
 * Negative numbers are wrapped in parentheses for accounting style:
 * -500 -> "($500)". Positive numbers get a leading "$".
 */
export function formatCurrency(amount: number, opts: { showCents?: boolean } = {}): string {
  const formatter = opts.showCents ? usdFormatterCents : usdFormatter;
  if (amount < 0) {
    // accounting style: ($500)
    return `(${formatter.format(Math.abs(amount))})`;
  }
  return formatter.format(amount);
}

/** Format a decimal rate (0.142) as "14.2%". */
export function formatPercent(rate: number): string {
  return percentFormatter.format(rate);
}

/**
 * Format with sign: refund = "+$1,234", owed = "-$500".
 * Used in BigNumber where the sign carries the meaning.
 */
export function formatSignedCurrency(amount: number, opts: { showCents?: boolean } = {}): string {
  if (amount === 0) return formatCurrency(0, opts);
  const abs = Math.abs(amount);
  const formatted = opts.showCents
    ? usdFormatterCents.format(abs)
    : usdFormatter.format(abs);
  return amount > 0 ? `+${formatted}` : `-${formatted}`;
}

/**
 * Parse a user-entered currency string into a number.
 * Accepts "1,234.56", "$1234", "(500)" for negatives, and whitespace.
 * Returns NaN for unparseable input.
 */
export function parseCurrencyInput(raw: string): number {
  if (typeof raw !== 'string') return NaN;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return NaN;

  let negative = false;
  let working = trimmed;
  if (working.startsWith('(') && working.endsWith(')')) {
    negative = true;
    working = working.slice(1, -1);
  }
  if (working.startsWith('-')) {
    negative = true;
    working = working.slice(1);
  }
  working = working.replace(/[$,\s]/g, '');

  if (!/^\d+(\.\d+)?$/.test(working)) return NaN;
  const n = Number(working);
  return negative ? -n : n;
}

/** Clamp a value to [min, max]. */
export function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

/** Round to nearest cent (avoid floating-point dust). */
export function roundCents(value: number): number {
  return Math.round(value * 100) / 100;
}